import React, { useEffect, useState } from 'react'
import { Badge, Button, Card, Container, Dropdown, Modal, Spinner } from 'react-bootstrap'
import { LuGraduationCap } from 'react-icons/lu'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import {
  BsEyeFill,
  BsPencilFill,
  BsThreeDotsVertical,
  BsTrashFill,
} from 'react-icons/bs'
import { useAuth } from './AuthContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import { useSidebar } from './SidebarContext'
import { isBusAttendantUser } from '../utils/busAttendant'
import {
  deleteAnnouncementRequest,
  fetchTopAnnouncements,
  getAnnouncementErrorMessage,
  parseAnnouncementListResponse,
} from './commonComponents/announcementApi'

function truncate(text, len = 80) {
  if (!text) return '—'
  const s = String(text)
  return s.length <= len ? s : `${s.slice(0, len)}…`
}

function formatDateTime(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function statusBadge(s) {
  const v = String(s || '').toLowerCase()
  if (v === 'published') return <Badge bg="success">Published</Badge>
  if (v === 'draft') return <Badge bg="secondary">Draft</Badge>
  if (v === 'archived') return <Badge bg="dark">Archived</Badge>
  return (
    <Badge bg="light" text="dark">
      {s || '—'}
    </Badge>
  )
}

function priorityBadge(p) {
  const v = String(p || '').toLowerCase()
  if (v === 'high') return <Badge bg="danger">High</Badge>
  if (v === 'mid') return <Badge bg="warning" text="dark">Mid</Badge>
  if (v === 'low') return <Badge bg="secondary">Low</Badge>
  return (
    <Badge bg="light" text="dark">
      —
    </Badge>
  )
}

export default function TopAnnouncements() {
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo, name: instituteName } = useInstitute()
  const isParentUser = user?.user_type === 'parent' || user?.user_type === 'parents'
  const isAttendantUser = isBusAttendantUser(user)
  const showAnnouncementEditDelete =
    user?.user_type !== 'teacher' && user?.user_type !== 'parents' && !isAttendantUser
  const showCreateAnnouncementButton =
    user?.user_type !== 'teacher' && user?.user_type !== 'parents' && !isAttendantUser && user?.user_type !== 'staff'
  const [announcements, setAnnouncements] = useState([])
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await fetchTopAnnouncements()
        if (cancelled) return
        setAnnouncements(parseAnnouncementListResponse(res))
      } catch (err) {
        if (!cancelled) {
          setAnnouncements([])
          toast.error(getAnnouncementErrorMessage(err, navigate))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [navigate])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const rowId = (r) => r.id ?? r.announcement_id

  const handleDelete = async () => {
    if (!deleteTarget?.id) return
    setDeleting(true)
    try {
      await deleteAnnouncementRequest(deleteTarget.id)
      setAnnouncements((prev) => prev.filter((r) => rowId(r) !== deleteTarget.id))
      setDeleteTarget(null)
      toast.success('Announcement deleted.')
    } catch (err) {
      toast.error(getAnnouncementErrorMessage(err, navigate))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
          .table td {
            overflow: visible !important;
            user-select: text;
          }
          .table tbody tr {
            overflow: visible !important;
            cursor: default;
          }
          .table-card {
            overflow: visible !important;
          }
          .table-wrapper {
            overflow-x: auto;
            overflow-y: visible !important;
            position: relative;
          }
          .table td, .table th { user-select: text; }
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }
          .table-card .dropdown-menu {
            z-index: 1050 !important;
            position: fixed !important;
          }
          .table-card .dropdown-menu.show {
            z-index: 1050 !important;
            position: fixed !important;
          }
          .table-card .dropdown-menu[data-popper-placement] {
            z-index: 1050 !important;
            position: fixed !important;
          }
          .table {
            overflow: visible !important;
          }
          @media (max-width: 768px) {
            .ann-message-cell {
              max-width: none !important;
              white-space: normal !important;
              word-break: break-word !important;
              overflow-wrap: anywhere !important;
              line-height: 1.45 !important;
            }
          }
        `}
      </style>
      <div
        style={{
          ...(isParentUser
            ? {
                marginLeft,
                transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'margin-left',
                WebkitTransition:
                  'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                zIndex: 1,
                marginTop: '0px',
                marginRight: '0px',
                marginBottom: '0px',
                paddingLeft: isCollapsed ? '62px' : '12px',
                paddingRight: isCollapsed ? '62px' : '12px',
              }
            : {
                marginLeft,
                transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'margin-left',
                position: 'relative',
                zIndex: 1,
                marginTop: '0px',
                paddingLeft: '12px',
                paddingRight: '12px',
              }),
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: isMobile ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          {isParentUser ? (
            <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  background: '#F3F4F6',
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  overflow: 'visible',
                  borderBottom: '1px solid #D1D3D4',
                  // width: '100%',
                }}
              >
                <div className="d-flex align-items-center gap-3" style={{ flex: 1 }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      background: '#223843',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {!logoError ? (
                      <img
                        src={logo}
                        alt="School Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                    )}
                  </div>

                  <div>
                    <h3
                      className="m-0 fw-bold text-start"
                      style={{
                        letterSpacing: '-0.02em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 'clamp(18px, 2.5vw, 22px)',
                        color: '#223843',
                        lineHeight: '1.2',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Announcements
                    </h3>
                    <p
                      className="m-0 mt-1 text-start"
                      style={{
                        fontSize: window.innerWidth < 768 ? '11px' : '13px',
                        color: '#6b7280',
                        fontWeight: '400',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      {instituteName || 'Institute'}
                    </p>
                  </div>
                </div>

                {!isMobile && <ProfileDropdown logout={logout} />}
              </div>
            </div>
          ) : (
            <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  background: '#F3F4F6',
                  paddingLeft: '28px',
                  paddingRight: '28px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  borderBottom: '1px solid #D1D3D4',
                  width: '100%',
                  gap: '12px',
                }}
              >
                <div
                  className="d-flex align-items-center gap-3"
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      background: '#223843',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {!logoError ? (
                      <img
                        src={logo}
                        alt="School Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={() => setLogoError(true)}
                      />
                    ) : (
                      <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h3
                      className="m-0 fw-bold page-title text-start"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                        lineHeight: '1.2',
                      }}
                    >
                      Announcements
                    </h3>
                    <p
                      className="m-0 mt-0 text-start"
                      style={{
                        fontSize: isMobile ? '10px' : '13px',
                        color: '#6b7280',
                      }}
                    >
                      {instituteName || 'Institute'}
                    </p>
                  </div>
                </div>

                {!isMobile && (
                  <div
                    className="d-flex align-items-center gap-3"
                    style={{ justifyContent: 'flex-end' }}
                  >
                    <ProfileDropdown
                      logout={logout}
                      notificationCount={3}
                      showNotifications={true}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <Container
          fluid
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '60vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '12px',
            paddingBottom: '32px',
          }}
        >
          <div className="d-flex justify-content-end mb-3">
          {showCreateAnnouncementButton && (
            <button
              type="button"
              className="d-none d-md-flex align-items-center justify-content-center"
              onClick={() => navigate('/announcement/create')}
              style={{
                borderRadius: '10px',
                padding: '6px 10px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: '650',
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Create New Announcement"
            >
              Create New Announcement
            </button>
            )}
          </div>

          <div className="d-md-none d-flex justify-content-end mb-3">
          {showCreateAnnouncementButton && (
            <button
              type="button"
              onClick={() => navigate('/announcement/create')}
              style={{
                borderRadius: '10px',
                padding: '6px 10px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                fontWeight: '650',
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Create New Announcement"
            >
              Create New Announcement
            </button>
            )}
          </div>

          <Card
            className="table-card"
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              overflow: 'visible',
              backgroundColor: '#ffffff',
              position: 'relative',
            }}
          >
            <div
              className="table-wrapper"
              style={{
                overflowX: 'auto',
                overflowY: 'visible',
                position: 'relative',
                borderRadius: '12px',
              }}
            >
              {loading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : announcements.length === 0 ? (
                <div className="text-center text-muted py-5 px-3" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                  No announcements yet.
                </div>
              ) : (
                <Table
                  className="table mb-0"
                  style={{
                    borderRadius: '12px',
                    overflow: 'visible',
                    position: 'relative',
                    marginBottom: '0',
                  }}
                >
                  <Thead>
                    <Tr style={{ backgroundColor: '#F3F4F6' }}>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Title</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Message</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Scope</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Status</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Priority</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Publish</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Expire</Th>
                      <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'center', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap' }}>Action</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {announcements.map((r, index) => {
                      const id = rowId(r)
                      const title = r.title || '—'
                      const msg = r.message ?? r.description ?? ''
                      const scope = r.scope ?? '—'
                      return (
                        <Tr
                          key={id ?? index}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                            transition: 'background-color 0.2s ease',
                            borderBottom: '1px solid #E5E7EB',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                          }}
                        >
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign: 'left', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, minWidth: '160px' }}>{title}</Td>
                          <Td className="ann-message-cell" style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign: 'left', fontFamily: 'Inter, system-ui, sans-serif', maxWidth: '280px' }}>
                            {truncate(msg)}
                          </Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign: 'left', fontFamily: 'Inter, system-ui, sans-serif', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{scope}</Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', textAlign: 'left', fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap' }}>
                            {statusBadge(r.status)}
                          </Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', textAlign: 'left', fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap' }}>
                            {priorityBadge(r.priority)}
                          </Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign: 'left', fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap' }}>
                            {formatDateTime(r.publish_at)}
                          </Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign: 'left', fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'nowrap' }}>
                            {formatDateTime(r.expire_at)}
                          </Td>
                          <Td className="text-center" style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>
                            <div className="d-none d-md-block">
                              <Dropdown
                                popperConfig={{
                                  modifiers: [
                                    {
                                      name: 'preventOverflow',
                                      options: {
                                        boundary: 'viewport',
                                        padding: 8,
                                      },
                                    },
                                    {
                                      name: 'flip',
                                      options: {
                                        fallbackPlacements: ['top', 'bottom', 'left', 'right'],
                                        boundary: 'viewport',
                                        padding: 8,
                                      },
                                    },
                                    {
                                      name: 'offset',
                                      options: {
                                        offset: [0, 8],
                                      },
                                    },
                                  ],
                                  strategy: 'fixed',
                                }}
                              >
                                <Dropdown.Toggle
                                  variant="light"
                                  id={`ann-actions-${id}`}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    backgroundColor: 'transparent',
                                    border: '1px solid #D1D3D4',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease',
                                  }}
                                  className="dropdown-toggle-no-caret"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#223843'
                                    e.currentTarget.style.borderColor = '#223843'
                                    e.currentTarget.style.color = '#ffffff'
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent'
                                    e.currentTarget.style.borderColor = '#D1D3D4'
                                    e.currentTarget.style.color = '#111827'
                                  }}
                                >
                                  <BsThreeDotsVertical style={{ color: 'inherit' }} />
                                </Dropdown.Toggle>

                                <Dropdown.Menu style={{ borderRadius: '8px', border: '1px solid #D1D3D4', padding: '8px', minWidth: '180px' }}>
                                  <Dropdown.Item
                                    onClick={() => navigate(`/announcement/view/${id}`)}
                                    style={{ fontSize: '13px', padding: '10px 12px', color: '#111827', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s ease', borderRadius: '6px', fontFamily: 'Inter, system-ui, sans-serif' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#223843'
                                      e.currentTarget.style.color = '#ffffff'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent'
                                      e.currentTarget.style.color = '#111827'
                                    }}
                                  >
                                    <BsEyeFill size={18} /> View
                                  </Dropdown.Item>
                                  {showAnnouncementEditDelete && (
                                    <>
                                      <Dropdown.Item
                                        onClick={() => navigate(`/announcement/edit/${id}`)}
                                        style={{ fontSize: '13px', padding: '10px 12px', color: '#111827', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s ease', borderRadius: '6px', fontFamily: 'Inter, system-ui, sans-serif' }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#10B981'
                                          e.currentTarget.style.color = '#ffffff'
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent'
                                          e.currentTarget.style.color = '#111827'
                                        }}
                                      >
                                        <BsPencilFill size={18} /> Edit
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => setDeleteTarget({ id, title })}
                                        style={{ fontSize: '13px', padding: '10px 12px', color: '#111827', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s ease', borderRadius: '6px', fontFamily: 'Inter, system-ui, sans-serif' }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#dc2626'
                                          e.currentTarget.style.color = '#ffffff'
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent'
                                          e.currentTarget.style.color = '#111827'
                                        }}
                                      >
                                        <BsTrashFill size={18} /> Delete
                                      </Dropdown.Item>
                                    </>
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>

                            <div className="d-md-none d-flex justify-content-start gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => navigate(`/announcement/view/${id}`)}
                                style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#223843', border: 'none', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: 'pointer' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.transform = 'scale(1.1)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.transform = 'scale(1)'
                                }}
                                title="View"
                              >
                                <BsEyeFill size={16} />
                              </button>
                              {showAnnouncementEditDelete && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/announcement/edit/${id}`)}
                                    style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#10B981', border: 'none', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: 'pointer' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#059669'
                                      e.currentTarget.style.transform = 'scale(1.1)'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#10B981'
                                      e.currentTarget.style.transform = 'scale(1)'
                                    }}
                                    title="Edit"
                                  >
                                    <BsPencilFill size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setDeleteTarget({ id, title })}
                                    style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#dc2626', border: 'none', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', cursor: 'pointer' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#b91c1c'
                                      e.currentTarget.style.transform = 'scale(1.1)'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#dc2626'
                                      e.currentTarget.style.transform = 'scale(1)'
                                    }}
                                    title="Delete"
                                  >
                                    <BsTrashFill size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              )}
            </div>
          </Card>
        </Container>
      </div>

      <Modal show={Boolean(deleteTarget)} onHide={() => !deleting && setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete announcement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget
            ? `Delete "${deleteTarget.title}"? This cannot be undone.`
            : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={deleting} onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" disabled={deleting} onClick={handleDelete}>
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
